import { TimeOfDay, WeatherType } from '../types';

export interface SkyWeights {
  noche: number;
  amanecer: number;
  dia: number;
  atardecer: number;
}

export interface CelestialState {
  // Solar
  isSunVisible: boolean;
  sunX: number; // 0 - 100 percentage
  sunY: number; // 0 - 100 percentage
  sunAltitude: number; // 0 to 1
  sunSize: number; // in px
  sunWarmOpacity: number;
  sunHaloOpacity: number;
  sunRaysOpacity: number;
  sunVisibleFactor: number;

  // Lunar
  isMoonVisible: boolean;
  moonX: number; // 0 - 100 percentage
  moonY: number; // 0 - 100 percentage
  moonAltitude: number; // 0 to 1
  moonVisibleFactor: number;

  // Atmospheric FX
  weights: SkyWeights;
  starsOpacity: number;
  auroraOpacity: number;
  cloudsOpacity: number;
  cloudsBrightness: number;
  cloudsHueRotate: number;
  cloudsSaturate: number;
  firefliesCount: number;
  mistOpacity: number;
  bloomOpacity: number;
  gardenTint: string;
  vignette: string;
  soilGradient: [string, string, string];
  phase: TimeOfDay;
  formattedTime: string;
  timeZone: string;
  minuteOfDay: number; // 0 - 1440
}

const M = (h: number, m = 0) => h * 60 + m;

// Key points of the 24-hour cycle with smoothstep transitions
// Identical to and improved from para-ti-mvd.com
const CLAVES: Array<{ t: number; w: Partial<SkyWeights> }> = [
  { t: M(0), w: { noche: 1 } },
  { t: M(4, 50), w: { noche: 1 } }, // Noche profunda
  { t: M(5, 0), w: { noche: 0.72, amanecer: 0.28 } }, // Primera luz / Lucero del alba
  { t: M(5, 35), w: { amanecer: 1 } }, // Amanecer pleno con tintes rosados y dorados
  { t: M(6, 0), w: { amanecer: 1 } },
  { t: M(6, 30), w: { dia: 1 } }, // Día completo
  { t: M(16, 40), w: { dia: 1 } },
  { t: M(17, 0), w: { dia: 0.55, atardecer: 0.45 } }, // Empieza el ocaso dorado
  { t: M(17, 35), w: { atardecer: 1 } }, // Atardecer pleno / Ocaso cálido y rojizo
  { t: M(18, 0), w: { atardecer: 1 } },
  { t: M(18, 30), w: { noche: 1 } }, // Crepúsculo final / Entrada a la noche estrellada
  { t: M(24), w: { noche: 1 } },
];

export function getSkyWeights(min: number): SkyWeights {
  const normalizedMin = ((min % 1440) + 1440) % 1440;
  let a = CLAVES[0];
  let b = CLAVES[CLAVES.length - 1];

  for (let i = 0; i < CLAVES.length - 1; i++) {
    if (normalizedMin >= CLAVES[i].t && normalizedMin <= CLAVES[i + 1].t) {
      a = CLAVES[i];
      b = CLAVES[i + 1];
      break;
    }
  }

  const span = b.t - a.t;
  let k = span === 0 ? 0 : (normalizedMin - a.t) / span;
  // Hermite smoothstep for natural, seamless optical gradient blending
  k = k * k * (3 - 2 * k);

  const w: SkyWeights = {
    noche: (a.w.noche || 0) + ((b.w.noche || 0) - (a.w.noche || 0)) * k,
    amanecer: (a.w.amanecer || 0) + ((b.w.amanecer || 0) - (a.w.amanecer || 0)) * k,
    dia: (a.w.dia || 0) + ((b.w.dia || 0) - (a.w.dia || 0)) * k,
    atardecer: (a.w.atardecer || 0) + ((b.w.atardecer || 0) - (a.w.atardecer || 0)) * k,
  };

  const total = w.noche + w.amanecer + w.dia + w.atardecer || 1;
  return {
    noche: w.noche / total,
    amanecer: w.amanecer / total,
    dia: w.dia / total,
    atardecer: w.atardecer / total,
  };
}

export function getPhaseFromMinute(min: number): TimeOfDay {
  const normalized = ((min % 1440) + 1440) % 1440;
  if (normalized >= M(5) && normalized < M(6, 30)) return 'dawn';
  if (normalized >= M(6, 30) && normalized < M(17)) return 'day';
  if (normalized >= M(17) && normalized < M(18, 30)) return 'sunset';
  return 'night';
}

export function getMinuteFromDate(date: Date = new Date()): number {
  return (
    date.getHours() * 60 +
    date.getMinutes() +
    date.getSeconds() / 60 +
    date.getMilliseconds() / 60000
  );
}

// Convert Phase enum to approximate default minute
export function getDefaultMinuteForPhase(phase: TimeOfDay): number {
  switch (phase) {
    case 'dawn':
      return M(5, 45); // 05:45 Amanecer
    case 'day':
      return M(12, 0); // 12:00 Mediodía
    case 'sunset':
      return M(17, 45); // 17:45 Atardecer
    case 'night':
    default:
      return M(22, 0); // 22:00 Noche
  }
}

export function calculateCelestialState(
  min: number,
  weather: WeatherType = 'sunny'
): CelestialState {
  const normalizedMin = ((min % 1440) + 1440) % 1440;
  const weights = getSkyWeights(normalizedMin);
  const phase = getPhaseFromMinute(normalizedMin);

  // 1. Solar calculations
  const salida = M(5, 0);
  const puesta = M(18, 30);
  let visibleSol = 0;
  let alturaSol = 0;
  let p = 0;

  if (normalizedMin > salida - 25 && normalizedMin < puesta + 25) {
    p = (normalizedMin - salida) / (puesta - salida);
    p = Math.max(0, Math.min(1, p));
    alturaSol = Math.sin(Math.PI * p);
    visibleSol = Math.max(0, Math.min(1, alturaSol * 6));
  }

  const weatherVeil =
    weather === 'rainy' ? 0.15 : weather === 'cloudy' ? 0.45 : 1;

  const sunVisibleFactor = visibleSol * weatherVeil;
  const sunX = 8 + 78 * p;
  const sunY = 64 - 52 * alturaSol;
  const sunSize = 62 + 28 * Math.pow(1 - alturaSol, 1.3);
  const sunWarmOpacity = Math.pow(1 - alturaSol, 1.4);
  const sunHaloOpacity = 0.35 + 0.45 * (1 - alturaSol);
  const sunRaysOpacity = 0.75 * alturaSol;

  // 2. Lunar calculations
  const lunaSale = M(18, 30);
  const lunaSePone = M(5, 0) + 1440;
  const mm = normalizedMin < M(5, 0) ? normalizedMin + 1440 : normalizedMin;
  let visibleLuna = 0;
  let alturaLuna = 0;
  let pl = 0;

  if (mm >= lunaSale - 40 && mm <= lunaSePone + 40) {
    pl = (mm - lunaSale) / (lunaSePone - lunaSale);
    pl = Math.max(0, Math.min(1, pl));
    alturaLuna = Math.sin(Math.PI * pl);
    visibleLuna =
      Math.max(0, Math.min(1, alturaLuna * 5)) *
      (weights.noche + weights.amanecer * 0.4 + weights.atardecer * 0.3);
  }

  const moonVisibleFactor = visibleLuna * (weather === 'sunny' ? 1 : weatherVeil * 1.3);
  const moonX = 92 - 74 * pl;
  const moonY = 60 - 48 * alturaLuna;

  // 3. Stars, Aurora & Clouds
  const starsOpacity = Math.min(
    1,
    (weights.noche * 1.0 + weights.atardecer * 0.35 + weights.amanecer * 0.3) *
      (weather === 'sunny' ? 1 : weather === 'cloudy' ? 0.3 : 0.05)
  );

  const auroraOpacity = weights.noche * 0.55 * (weather === 'sunny' ? 1 : 0.2);

  const cloudsOpacity =
    weights.dia + weights.amanecer * 0.85 + weights.atardecer * 0.75 + weights.noche * 0.15;
  const cloudsBrightness =
    weights.dia * 1.0 + weights.amanecer * 0.92 + weights.atardecer * 0.85 + weights.noche * 0.45;
  const cloudsHueRotate = -(weights.amanecer * 14 + weights.atardecer * 22);
  const cloudsSaturate = 1 + weights.atardecer * 0.3;

  // 4. Fireflies count (high during twilight and deep starry night)
  const firefliesCount = Math.round(
    (weights.noche * 14 + weights.atardecer * 10) * (weather === 'rainy' ? 0.2 : 1)
  );

  // 5. Environmental Lighting & Tint
  const mistOpacity = weights.amanecer * 0.95 + weights.noche * 0.6 + weights.atardecer * 0.4;
  const bloomOpacity = weights.noche * 1.0 + weights.atardecer * 0.8 + weights.amanecer * 0.5 + weights.dia * 0.2;

  // Ambient Garden Tint
  const rTint = Math.round(40 * weights.noche + 255 * weights.amanecer + 255 * weights.dia + 255 * weights.atardecer);
  const gTint = Math.round(70 * weights.noche + 170 * weights.amanecer + 250 * weights.dia + 146 * weights.atardecer);
  const bTint = Math.round(140 * weights.noche + 130 * weights.amanecer + 225 * weights.dia + 66 * weights.atardecer);
  const aTint = (0.3 * weights.noche + 0.26 * weights.amanecer + 0.1 * weights.dia + 0.32 * weights.atardecer).toFixed(2);
  const gardenTint = `rgba(${rTint}, ${gTint}, ${bTint}, ${aTint})`;

  // Vignette
  const vignette = `rgba(0, 0, 0, ${(0.55 * weights.noche + 0.3 * weights.amanecer + 0.15 * weights.dia + 0.45 * weights.atardecer).toFixed(2)})`;

  // Soil gradients
  const soilGradient: [string, string, string] = [
    `rgba(${Math.round(58 * weights.noche + 107 * weights.amanecer + 125 * weights.dia + 96 * weights.atardecer)}, ${Math.round(39 * weights.noche + 72 * weights.amanecer + 87 * weights.dia + 60 * weights.atardecer)}, ${Math.round(24 * weights.noche + 48 * weights.amanecer + 53 * weights.dia + 36 * weights.atardecer)}, 1)`,
    `rgba(${Math.round(27 * weights.noche + 67 * weights.amanecer + 81 * weights.dia + 54 * weights.atardecer)}, ${Math.round(16 * weights.noche + 43 * weights.amanecer + 53 * weights.dia + 32 * weights.atardecer)}, ${Math.round(9 * weights.noche + 26 * weights.amanecer + 32 * weights.dia + 16 * weights.atardecer)}, 1)`,
    `rgba(${Math.round(7 * weights.noche + 41 * weights.amanecer + 51 * weights.dia + 26 * weights.atardecer)}, ${Math.round(4 * weights.noche + 26 * weights.amanecer + 32 * weights.dia + 14 * weights.atardecer)}, ${Math.round(3 * weights.noche + 15 * weights.amanecer + 15 * weights.dia + 7 * weights.atardecer)}, 1)`,
  ];

  // Time formatting
  const hours = Math.floor(normalizedMin / 60);
  const mins = Math.floor(normalizedMin % 60);
  const formattedTime = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;

  let timeZone = 'Hora local';
  try {
    timeZone = (Intl.DateTimeFormat().resolvedOptions().timeZone || 'Hora local').replace(/_/g, ' ');
  } catch {
    timeZone = 'Hora local';
  }

  return {
    isSunVisible: sunVisibleFactor > 0.02,
    sunX,
    sunY,
    sunAltitude: alturaSol,
    sunSize,
    sunWarmOpacity,
    sunHaloOpacity,
    sunRaysOpacity,
    sunVisibleFactor,

    isMoonVisible: moonVisibleFactor > 0.02,
    moonX,
    moonY,
    moonAltitude: alturaLuna,
    moonVisibleFactor,

    weights,
    starsOpacity,
    auroraOpacity,
    cloudsOpacity,
    cloudsBrightness,
    cloudsHueRotate,
    cloudsSaturate,
    firefliesCount,
    mistOpacity,
    bloomOpacity,
    gardenTint,
    vignette,
    soilGradient,
    phase,
    formattedTime,
    timeZone,
    minuteOfDay: normalizedMin,
  };
}
